import type { TokenStore } from '@hrms/api-client';
import type { TokenPair } from '@hrms/shared';

export interface WebTokenStore extends TokenStore {
  /** True when a refresh token is persisted — i.e. there is a session worth trying to resume. */
  hasSession(): boolean;
  /** Fires on save(), clear(), and when another tab changes the persisted token. */
  subscribe(listener: () => void): () => void;
}

/** The subset of Storage this store uses — `localStorage` in the browser, a Map-backed fake in tests. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const REFRESH_KEY = 'hrms.refresh';

/**
 * Access token in memory, refresh token in localStorage.
 *
 * WHY NOT BOTH IN MEMORY: a reload would log the user out. That was acceptable when the admin
 * console was its own short-lived app; it is not for the long-lived employee session the admin
 * area now lives inside.
 *
 * WHY NOT BOTH IN LOCALSTORAGE: whatever is in localStorage is one XSS away from exfiltration. The
 * access token is the one that authorises requests directly, so it stays in a closure. The refresh
 * token is the smaller exposure — it is opaque, single-use, rotated on every refresh, and the
 * server revokes the whole family the moment a consumed one is presented again
 * (packages/auth/src/tokens.ts). That reuse-detection is the backstop this trade-off leans on.
 *
 * WHY READ-THROUGH: `getRefreshToken()` re-reads storage on every call rather than caching. The
 * cross-tab refresh lock in @hrms/api-client relies on this — the tab that wins the lock rotates
 * the token and writes the new one, and the tab that waited must then read THAT, not the stale
 * one it saw before the lock. A cached value would make the second tab present a consumed token
 * and trip reuse-detection, logging both tabs out.
 *
 * Every storage access is wrapped: `localStorage` throws in some private modes and when site data
 * is blocked, and a throw here would take the whole app down at boot rather than degrade to a
 * memory-only session.
 */
export function createWebTokenStore(
  storage: KeyValueStorage | null = safeLocalStorage(),
): WebTokenStore {
  let accessToken: string | null = null;
  const listeners = new Set<() => void>();

  const read = (): string | null => {
    try {
      return storage?.getItem(REFRESH_KEY) ?? null;
    } catch {
      return null;
    }
  };
  const write = (value: string | null): void => {
    try {
      if (value === null) storage?.removeItem(REFRESH_KEY);
      else storage?.setItem(REFRESH_KEY, value);
    } catch {
      // Memory-only for this session; the next reload will simply require signing in again.
    }
  };
  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      // Another tab signed in or out. The access token in THIS tab's memory is now stale either
      // way (a sign-out revoked its family; a sign-in belongs to a different session), so drop it
      // and let the next request refresh from whatever is persisted now.
      if (event.key === REFRESH_KEY || event.key === null) {
        accessToken = null;
        notify();
      }
    });
  }

  return {
    getAccessToken: () => accessToken,
    getRefreshToken: read,
    save: (tokens: TokenPair) => {
      accessToken = tokens.accessToken;
      write(tokens.refreshToken);
      notify();
    },
    clear: () => {
      accessToken = null;
      write(null);
      notify();
    },
    hasSession: () => read() !== null,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function safeLocalStorage(): KeyValueStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}
